import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Box, Button, Flex, Text } from 'theme-ui';
import moment from 'moment';
import { ColumnDef } from '@tanstack/react-table';
import { LuPenLine, LuPlus, LuTrash2 } from 'react-icons/lu';
import { FaLink } from 'react-icons/fa';

import { IContract, ISalesRepInfo, ContractsListModalProps } from '../../../@types';
import { createContract, updateContract, deleteContract } from '../../store/actions/project.action';
import DataTable from '../Tabels/Tabel';
import { ContractModal } from '../pages/createProject/StepOne/ContractModal';
import { bucketUrl } from '../../utils/global';

function isSalesRepInfo(obj: unknown): obj is ISalesRepInfo {
  if (typeof obj !== 'object' || obj === null) return false;
  const o = obj as Record<string, unknown>;
  return (
    ('FirstName' in o && typeof o.FirstName === 'string') ||
    ('LastName' in o && typeof o.LastName === 'string') ||
    ('_id' in o && typeof o._id === 'string')
  );
}

export function ContractsListModal({
  isOpen,
  toggle,
  projectId,
  contracts = [],
  salesReps,
  colorMode,
}: ContractsListModalProps) {
  const dispatch = useDispatch();
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [editingContract, setEditingContract] = useState<IContract | null>(null);
  const [contractToDelete, setContractToDelete] = useState<IContract | null>(null);

  const isDark = colorMode === 'dark';
  const modalBg = isDark ? '#20283c' : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#0a0a0a';
  const rowBorderColor = isDark ? '#2d3748' : '#e2e8f0';

  const handleAddContract = (newContract: IContract) => {
    return new Promise<void>((resolve, reject) => {
      dispatch(
        createContract(
          projectId,
          newContract,
          (res: { status: boolean; data?: IContract; message?: string }) => {
            if (res?.status) {
              resolve();
            } else {
              reject(new Error(res?.message || 'Failed to create contract'));
            }
          }
        )
      );
    });
  };

  const handleUpdateContract = (updatedContract: IContract) => {
    return new Promise<void>((resolve, reject) => {
      if (!updatedContract._id) {
        reject(new Error('Contract ID is missing'));
        return;
      }
      dispatch(
        updateContract(
          projectId,
          updatedContract._id,
          updatedContract,
          (res: { status: boolean; data?: IContract; message?: string }) => {
            if (res?.status) {
              resolve();
            } else {
              reject(new Error(res?.message || 'Failed to update contract'));
            }
          }
        )
      );
    });
  };

  const handleDeleteClick = (contract: IContract) => {
    if (!contract._id) return;
    setContractToDelete(contract);
  };

  const handleConfirmDelete = () => {
    if (!contractToDelete?._id) return;
    dispatch(
      deleteContract(projectId, contractToDelete._id, (res: { status: boolean; message?: string }) => {
        if (!res?.status) {
          alert(res?.message || 'Failed to delete contract');
        }
      })
    );
    setContractToDelete(null);
  };

  const handleCancelDelete = () => {
    setContractToDelete(null);
  };

  const getSalesRepName = (rep: unknown): string => {
    if (!rep) return '-';
    const repId = typeof rep === 'object' && rep !== null ? (rep as { _id?: string })._id : (typeof rep === 'string' ? rep : null);
    if (repId) {
      const foundRep = salesReps.find((u) => u._id === repId);
      return foundRep ? `${foundRep.FirstName || ''} ${foundRep.LastName || ''}`.trim() : '-';
    }
    return '-';
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '-';
    const m = moment(dateStr);
    return m.isValid() ? m.format('MMMM DD, YYYY') : dateStr;
  };

  const renderAttachmentLinks = (attachments: string[] | undefined) => {
    if (!attachments || attachments.length === 0) return '-';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
        {attachments.map((url, idx) => {
          if (!url) return null;
          const fullUrl = `${bucketUrl}${url}`

          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: isDark ? '#0dcaf0' : '#0d6efd',
                cursor: 'pointer',
              }}
              onClick={() => window.open(fullUrl, '_blank')}
            >
              <FaLink size={14} color="#0d6efd" />
              <span style={{ color: isDark ? '#0dcaf0' : 'inherit', textDecoration: 'underline' }}>
                LINK-{idx + 1}
              </span>
            </Box>
          );
        })}
      </Box>
    );
  };

  const columns = useMemo<ColumnDef<IContract>[]>(() => [
    {
      header: 'Proposal Name',
      accessorKey: 'proposalName',
      cell: ({ row }) => <Text variant="Poppins14Normal24lh">{row.original.proposalName}</Text>,
    },
    {
      header: 'Date of Approval',
      accessorKey: 'dateOfApproval',
      cell: ({ row }) => <Text variant="Poppins14Normal24lh">{formatDate(row.original.dateOfApproval)}</Text>,
    },
    {
      header: 'Signed Contract',
      accessorKey: 'attachments',
      cell: ({ row }) => (
        <Box>{renderAttachmentLinks(row.original.attachments)}</Box>
      ),
    },
    {
      header: 'Sales Representative',
      accessorKey: 'salesRepresentative',
      cell: ({ row }) => <Text variant="Poppins14Normal24lh">{getSalesRepName(row.original.salesRepresentative)}</Text>,
    },
    {
      header: 'Actions',
      id: 'actions',
      cell: ({ row }) => {
        const contract = row.original;
        return (
          <Flex sx={{ gap: '16px', justifyContent: 'center' }}>
            <LuPenLine
              style={{ cursor: 'pointer', color: '#7743DB', width: '20px', height: '20px' }}
              onClick={() => setEditingContract(contract)}
              title="Edit"
            />
            <button
              onClick={() => handleDeleteClick(contract)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                color: '#dc3545',
                display: 'flex',
                alignItems: 'center',
              }}
              title="Delete"
            >
              <LuTrash2 size={18} />
            </button>
          </Flex>
        );
      },
    },
  ], [salesReps]);

  return (
    <>
      <Modal isOpen={isOpen} toggle={toggle} centered size="xl" scrollable>
        <ModalHeader
          toggle={toggle}
          style={{
            backgroundColor: modalBg,
            color: textColor,
            borderBottom: `1px solid ${rowBorderColor}`,
          }}
        >
          <Flex sx={{ justifyContent: 'space-between', alignItems: 'center', width: '95%' }}>
            <span style={{ fontSize: '18px', fontWeight: 600, letterSpacing: '0.05em' }}>
              Contracts
            </span>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              sx={{
                backgroundColor: '#7743DB',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: '#5f32b3',
                },
              }}
            >
              <LuPlus size={16} />
              Add Contract
            </Button>
          </Flex>
        </ModalHeader>
        <ModalBody style={{ backgroundColor: modalBg, color: textColor, padding: '24px' }}>
          {contracts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Text sx={{ fontSize: '14px', color: '#6c757d' }}>No contracts added yet.</Text>
            </Box>
          ) : (
            <DataTable
              data={contracts}
              columns={columns}
              showNavigation={false}
              enableColumnFilters={false}
              perPageCount={contracts.length || 10}
              onPageChange={() => { }}
              onPerPageChange={() => { }}
              noDataCentered
            />
          )}
        </ModalBody>
      </Modal>

      <ContractModal
        isOpen={isAddModalOpen || !!editingContract}
        toggle={() => {
          setIsAddModalOpen(false);
          setEditingContract(null);
        }}
        onAdd={editingContract ? handleUpdateContract : handleAddContract}
        salesReps={salesReps}
        contract={editingContract}
      />

      <Modal isOpen={!!contractToDelete} toggle={handleCancelDelete} size="sm" centered>
        <ModalHeader toggle={handleCancelDelete} style={{ backgroundColor: modalBg, color: textColor }}>Confirm Deletion</ModalHeader>
        <ModalBody style={{ backgroundColor: modalBg, color: textColor }}>
          {`Are you sure you want to delete the contract "${contractToDelete?.proposalName || ''}"?`}
        </ModalBody>
        <ModalFooter style={{ backgroundColor: modalBg }}>
          <Button variant="CancelButton" sx={{ mr: 2, cursor: 'pointer' }} onClick={handleCancelDelete}>No</Button>
          <Button type="button" variant="SaveButton" sx={{ cursor: 'pointer' }} onClick={handleConfirmDelete}>Yes</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
